// The contents of this file were generated with the assistance of AI

import React from "react";

// Helper for rendering markdown texts from Strapi correctly (which come in JSON format, e.g. { text: 'Hallo', italic: true, bold: true, url: 'https://...' })
const renderLeaf = (leaf, key) => {

  if (leaf == null) return null;

  const text = leaf.text ?? "";
  let node = text;

  // Check for formatting options
  if (leaf.code) node = <code key={key}>{node}</code>;
  if (leaf.bold) node = <strong key={key}>{node}</strong>;
  if (leaf.italic) node = <em key={key}>{node}</em>;
  if (leaf.underline) node = <u key={key}>{node}</u>;
  if (leaf.strikethrough) node = <s key={key}>{node}</s>;

  // If there is a link, wrap it with an <a> tag
  const href = leaf.url ?? leaf.href ?? leaf.link;
  if (href) {
    node = (
      <a
        key={key}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
      >
        {node}
      </a>
    );
  }

  return <React.Fragment key={key}>{node}</React.Fragment>;
};

// Checking individual nodes
const renderNode = (node, key) => {
    if (node == null) return null;

    // Plaintext
    if (typeof node.text === "string") {
        return renderLeaf(node, key);
    }

    // Regular string
    if (typeof node === "string") {
        return <p key={key}>{node}</p>;
    }

    // If the node contains further nodes: recursion
    const children = (node.children || []).map((child, idx) =>
        renderNode(child, `${key}-${idx}`)
    );

    // Heuristics: Use node.type if it exists, otherwise <p>
    switch (node.type) {
        case "paragraph":
        case "p":
        case undefined: // Top-level nodes sometimes don't have a type
            return (
                <p key={key}>
                    {children}
                </p>
            );

        case "heading":
        case "heading-1":
        case "h1":
            return <h1 key={key}>{children}</h1>;
        case "heading-2":
        case "h2":
            return <h2 key={key}>{children}</h2>;
        case "heading-3":
        case "h3":
            return <h3 key={key}>{children}</h3>;

        case "bulleted-list":
        case "ul":
            return <ul key={key}>{children}</ul>;
        case "numbered-list":
        case "ol":
            return <ol key={key}>{children}</ol>;
        case "list-item":
        case "li":
            return <li key={key}>{children}</li>;

        case "link":
        case "a":
            return (
                <a
                    key={key}
                    href={node.url ?? node.href ?? node.link}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {children}
                </a>
            );

        case "image":
            {
                const src = node.url ?? node.src ?? node.attributes?.url;
                const alt = node.alt ?? node.attributes?.alternativeText ?? "";
                if (!src) return null;
                return <img key={key} src={src} alt={alt} />;
            }

        // Fallback: wrap with <div>
        default:
            return (
                <div key={key} data-type={node.type}>
                    {children}
                </div>
            );
    }
};

export default function RichTextRenderer({ value }) {
    if (value == null) return null;

    // In case an object comes with "data" (e.g., relations or nested fields)
    if (value.data) {
        const attrs = value.data;

        // Catch common richtext fields
        const possible = attrs.description ?? attrs.content ?? Object.values(attrs).find(v => Array.isArray(v));
        if (Array.isArray(possible)) {
            return <>{possible.map((n, i) => renderNode(n, `node-${i}`))}</>;
        }
    }

    // Usually, value will be a direct array of richtext nodes
    const nodes = Array.isArray(value) ? value : [value];

    return <>{nodes.map((n, i) => renderNode(n, `root-${i}`))}</>;
}
